import { Component, Input } from '@angular/core';

export type CxRingSize = 66 | 50 | 18;

/** Stroke by ring size: P6:1535 `ring(p, 66, 6)`, P6:1630 `ring(.., 50, 5)`, P6:1540 miniRing 18 (2.5, P6:1542 draws 2.4). */
const STROKE_BY_SIZE: Readonly<Record<CxRingSize, number>> = { 66: 6, 50: 5, 18: 2.5 };

export interface RingGeometryResult {
    readonly Stroke: number;
    readonly R: number;
    readonly Circumference: number;
    /** `stroke-dasharray` visible run: `c * p / 100` (P6:1538). */
    readonly Dash: number;
}

/** The ring's circle maths (P6:1535-1538): `r = (size - stroke) / 2`, `c = 2πr`, dash `c * p / 100`; `p` is clamped to 0..100. */
export function RingGeometry(size: CxRingSize, percent: number): RingGeometryResult {
    const stroke = STROKE_BY_SIZE[size];
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const p = Math.min(100, Math.max(0, percent));
    return { Stroke: stroke, R: r, Circumference: c, Dash: (c * p) / 100 };
}

/**
 * The progress ring (P6:350-352 `.cx-ring`, :1535-1538 `ring()`). The 66 px ring prints `Label` in its centre; the 50 and 18 px
 * rings are unlabelled (the row or tab beside them names the value). `Percent` is 0..100; the label text comes from the caller
 * (the contract's `PercentLabel`), never composed here.
 */
@Component({
    standalone: false,
    selector: 'ra-cx-ring',
    templateUrl: './cx-ring.component.html',
    styleUrl: './cx-ring.component.scss',
})
export class CxRingComponent {
    @Input() Percent = 0;
    @Input() Size: CxRingSize = 66;
    @Input() Label: string | null = null;
    /** The 18 px tab ring turns green once everything is reviewed (P6:1541 `p>=1 ? ok : cx`). */
    @Input() Complete = false;

    get Geometry(): RingGeometryResult { return RingGeometry(this.Size, this.Percent); }
    get ShowLabel(): boolean { return this.Size === 66 && this.Label !== null; }
}
